/**
 * The console's configuration screens (WALLIYO-0035): **Fonctionnalités**
 * (customer feature enablements, switched by scope) and **Règles** (company
 * rules, versioned). Imported as `@wallyo/contracts/console/configuration`.
 *
 * Like every console endpoint, each answers under its own response name
 * (WALLIYO-0068), fails with a `ConsoleErrorResponse`, takes a `reason` on
 * every write (it goes on the audit entry) and an `Idempotency-Key` header,
 * generated once per click (ADR-0016 §2.1).
 */

import type { StaffFeatureId, UserFeatureId } from "../actions/types";
import type { CustomerSettingSource } from "./console-accounts";
import type { DefinedRuleId } from "../rules/definitions";
import type { RuleCategory } from "../rules/types";
import type { SettingId } from "../settings/definitions";
import type { CustomerScope, Sensitivity, SettingCategory } from "../settings/types";
import type { SettingValue, Unit, ValueTypeName } from "../settings/value-types";

// ── Fonctionnalités: customer feature enablements (ADR-0013) ──────────────
//
// Needs MANAGE_APP_FEATURES_STAFF_FEATURE_ENABLEMENT, for reads and writes.
// Writes are SET_USER_FEATURE_ENABLEMENT_STAFF_ACTION, audited with the value
// before and after. A feature's `sensitivity` decides who may change it:
// - `low` and `high`: anyone holding MANAGE_APP_FEATURES, itself a high
//   feature, so in practice an Ops Manager or the Owner;
// - `critical`: an Owner only (MANAGE_ROLES plus every critical staff
//   feature), and the change is reported to the Owners.

export type CustomerFeatureScope = "system" | CustomerScope;

/**
 * Scopes a definition allows but the server can't store yet. `clientProfile`
 * has no column in `setting_values` because there is no client_profiles
 * table to point at (migration 018): a write at this scope is refused with
 * `scope_not_supported`. Use `user` for one person until client profiles exist.
 */
export const UNSUPPORTED_FEATURE_SCOPES = ["clientProfile"] as const satisfies readonly CustomerFeatureScope[];

/** The regions a `region` value can name. */
export const FEATURE_REGIONS = ["gn", "sn", "us", "eu"] as const;
export type FeatureRegion = (typeof FEATURE_REGIONS)[number];

/** Who a value applies to. Exactly one target per scope. */
export type FeatureTarget =
  | { scope: "system" }
  | { scope: "allClients" }
  | { scope: "allPros" }
  | { scope: "region"; region: FeatureRegion }
  /** Everyone on this app version or newer, e.g. "2.4.0". */
  | { scope: "appVersion"; minAppVersion: string }
  | { scope: "user"; userId: string }
  /** Today a pro profile is a row of `workers`. */
  | { scope: "proProfile"; proProfileId: string };

/**
 * Whose features the screen is showing.
 *
 * Either everyone of a kind, or one user by their UUID — the number, not the
 * key, because that is what a staff member has in front of them: on a badge,
 * from a support call, or copied out of the audit.
 *
 * The scope a value is written at follows from this, so nobody picks a scope:
 * you pick who, and "all clients" is a choice in the same list as a user.
 */
export type FeatureSubject = { kind: "allClients" } | { kind: "allPros" } | { kind: "user"; uuid: number };

export type CustomerFeatureListItem = {
  settingId: UserFeatureId;
  category: SettingCategory;
  /** What applies where no value is set. */
  defaultValue: boolean;
  /** `planned`: agreed, not built yet. A value can be set, but no code reads it yet. */
  status: "planned" | "active";
  sensitivity: Sensitivity;
  /** Every scope the definition allows. */
  scopes: readonly CustomerFeatureScope[];
  /** The allowed scopes a value can be set at today: `scopes` minus UNSUPPORTED_FEATURE_SCOPES. */
  settableScopes: readonly CustomerFeatureScope[];
  /** How many values are set for it, at any scope, expired ones included. */
  exceptionCount: number;
  /**
   * What this subject actually gets, resolved the way the app resolves it —
   * the most specific scope wins. Absent when no subject was asked for.
   */
  value?: boolean;
  /**
   * Which scope's value won — the same `source` a resolved setting carries.
   *
   * It is what the toggle means. A row sourced at the subject's own scope is
   * a decision someone made about them, and the only one they can be released
   * from: flipping a row sourced anywhere else writes a new override, which
   * can then be handed back to the broader scope. `default` means nobody has
   * set it anywhere.
   */
  source?: CustomerSettingSource;
  /**
   * Whether *this caller* could save a change to this row, for this subject.
   *
   * There is no read-only role: it is the write's own rules, evaluated on the
   * read so the toggle can be disabled before anyone presses it. A `critical`
   * feature is not editable by a non-Owner; nor is a row whose scope cannot
   * hold a value. The write evaluates the same rules again and refuses
   * anyway, so a screen can never offer a toggle the save would reject.
   */
  editable?: boolean;
  /** Why not, for the console to translate. Absent when `editable`. */
  notEditable?: "critical_requires_owner" | "scope_not_settable";
  /**
   * The version of the value set at the subject's own scope, or absent when
   * none is set there.
   *
   * Send it back with the change. It is what makes a Save safe: a row that
   * somebody else moved while this screen was open refuses, rather than
   * silently overwriting their decision. Absent means "there was nothing
   * here", and a row that has since appeared refuses just the same.
   */
  valueVersion?: number;
};

/**
 * GET /api/v1/console/features
 *
 * Every customer feature enablement that isn't retired, by category then id.
 * A plain read: not audited.
 */
export type ListCustomerFeaturesResponse = {
  features: readonly CustomerFeatureListItem[];
  /**
   * Who the values belong to, echoed back with the name resolved — so the
   * screen can say "Mariama Bah" over a table the caller asked for by number,
   * and a number that matches nobody is an answer rather than an empty table.
   */
  subject?: { uuid: number; name: string; kind: "client" | "pro" } | null;
};

export type CustomerFeatureValue = {
  settingValueId: string;
  settingId: UserFeatureId;
  target: FeatureTarget;
  /** The person's or pro profile's name, for `user` and `proProfile`; else null. */
  targetName: string | null;
  value: boolean;
  reason: string | null;
  /** Null: until changed. */
  expiresAt: string | null;
  /** True once `expiresAt` has passed: the value no longer applies. */
  expired: boolean;
  updatedAt: string;
  /** Send it back to change or remove this value: a stale one is refused. */
  version: number;
};

/**
 * GET /api/v1/console/features/:settingId/values
 *
 * Every value set for it, broadest scope first. A plain read: not audited.
 */
export type ListCustomerFeatureValuesResponse = {
  feature: CustomerFeatureListItem;
  values: readonly CustomerFeatureValue[];
};

/**
 * PUT /api/v1/console/features/:settingId/values
 *
 * Sets the value at one scope and target, creating it or changing the one
 * already there. `version`:
 * - leave it out to create: if a value already exists for that target, the
 *   write is refused with `stale_version` (409);
 * - send the existing value's version to change it: if it moved on, or was
 *   removed, it is refused with `stale_version` too.
 *
 * Refused with `validation_error` (400) for a scope the feature doesn't allow
 * (`scope_not_allowed`), for `clientProfile` (`scope_not_supported`, see
 * UNSUPPORTED_FEATURE_SCOPES), for an expiry in the past, and with 404 for a
 * person or pro profile that doesn't exist.
 */
export type SetCustomerFeatureValueRequest = {
  target: FeatureTarget;
  value: boolean;
  reason: string;
  /** ISO 8601 date-time in the future. Null or left out: until changed. */
  expiresAt?: string | null;
  version?: number;
};

export type SetCustomerFeatureValueResponse = {
  value: CustomerFeatureValue;
};

/**
 * DELETE /api/v1/console/features/:settingId/values/:settingValueId
 *
 * Removes the value, so the next broader scope (or the default) applies again.
 * A stale `version` is refused with `stale_version` (409).
 */
export type RemoveCustomerFeatureValueRequest = {
  reason: string;
  version: number;
};

export type RemoveCustomerFeatureValueResponse = {
  removed: { settingValueId: string };
};

/**
 * One row of a Save: which feature, and what it should say for the subject.
 *
 * `value: null` is the third state, and the only way back: it removes the
 * value at the subject's own scope, so the next broader scope (or the
 * default) applies again. Clear, off, on.
 *
 * `version` is the one the screen was showing. Send it when a value was
 * already set there, and leave it out when none was — the two mismatches
 * (someone else set one, someone else removed one) are both refused.
 */
export type CustomerFeatureChange = {
  settingId: UserFeatureId;
  value: boolean | null;
  version?: number;
  /** When it stops applying. Null, or absent, means until changed. */
  expiresAt?: string | null;
};

/**
 * PUT /api/v1/console/features/values
 *
 * One Save, one transaction. The subject decides the scope the values are
 * written at, so nobody picks a scope: you pick who.
 *
 * Every change is authorized before any of them runs, and each writes its own
 * audit entry inside the one transaction. Anything fails — a stale version on
 * the last row — and none of it is saved. One `reason` covers the save, and
 * the `Idempotency-Key` header makes a double-click harmless.
 */
export type SaveCustomerFeaturesRequest = {
  subject: FeatureSubject;
  reason: string;
  changes: readonly CustomerFeatureChange[];
};

/**
 * What the screen should now show: the subject's features re-resolved, exactly
 * as `GET /features` would answer them. A save at `allClients` changes where
 * other rows get their value from, so handing back the whole table is the only
 * answer that can't be stale.
 */
export type SaveCustomerFeaturesResponse = ListCustomerFeaturesResponse & {
  /** How many values were written, changed or removed. */
  saved: number;
};

// ── Feature Enablements, Walliyo console (ADR-0013 §2.3, ADR-0014 §2.5) ───
//
// The same screen as the app tab, over the staff catalogue. It sets the two
// scopes that belong to a person rather than to a role: `allStaff`, the floor
// under everyone, and `staffMember`, one person's override.
//
// Role grants stay in Rôles & équipe, because revoking one there also revokes
// the permissions hanging off it. An override does not cascade: it changes
// what this person resolves, and the role keeps whatever it granted.

/**
 * Whose console features the screen is showing: one employee, by their UUID.
 *
 * There is no "all staff" record. A console feature is off until granted, and
 * granting is what roles are for — a value at `allStaff` would be a role
 * nobody could be removed from. Nothing has ever written one. What this
 * screen is for is the other half: the override on one person, which had no
 * home before.
 */
export type StaffFeatureSubject = { uuid: number };

/**
 * Where a console feature's answer came from, in the resolver's own order
 * (staff-features.ts): an override on the person wins, then any role granting
 * it, then the definition's default — which for a staff feature is always
 * off. Those three are what a reader sees.
 *
 * `allStaff` is kept because the resolver still checks that scope. No screen
 * writes one, so it should never appear — but if one ever does, the source
 * has to be able to say so rather than call it a default.
 */
export type StaffFeatureSource = "staffMember" | "staffRole" | "allStaff" | "default";

export type StaffFeatureListItem = {
  settingId: StaffFeatureId;
  category: SettingCategory;
  status: "planned" | "active";
  sensitivity: Sensitivity;
  /** What this subject actually gets, resolved the way the console resolves it. */
  value: boolean;
  source: StaffFeatureSource;
  /** The roles granting it, when that is where the answer came from. */
  grantedBy: readonly string[];
  /** Whether this caller could save a change to this row, for this subject. */
  editable: boolean;
  notEditable?: "critical_requires_owner" | "own_account";
  /** The version of the value set at the subject's own scope, absent when none is. */
  valueVersion?: number;
};

/**
 * GET /api/v1/console/staff-features?uuid=27217102
 *
 * One employee's console features. A plain read.
 */
export type ListStaffFeaturesResponse = {
  features: readonly StaffFeatureListItem[];
  /** The employee the number resolved to, echoed back with their roles. */
  subject: { uuid: number; name: string; roles: readonly string[] };
};

export type StaffFeatureChange = {
  settingId: StaffFeatureId;
  /** `null` removes the value at this scope, so the person follows their roles again. */
  value: boolean | null;
  version?: number;
};

/** PUT /api/v1/console/staff-features/values — one Save, one transaction. */
export type SaveStaffFeaturesRequest = {
  subject: StaffFeatureSubject;
  reason: string;
  changes: readonly StaffFeatureChange[];
};

export type SaveStaffFeaturesResponse = ListStaffFeaturesResponse & { saved: number };

// ── User Settings (ADR-0013 §2.4) ────────────────────────────────────────
//
// Somebody's own choices: their theme, their language, a pro's lead radius,
// an employee's console theme. Two sub-tabs over two catalogues, and one
// shape, because a setting has no group record — it is a person's choice, so
// the only scope that matters here is their own.
//
// Unlike a feature enablement, a setting is not a boolean. Its `valueType`
// says what it is, and the screen renders the editor that type deserves.

/** Which catalogue a sub-tab shows. `app` is a customer, `employee` is staff. */
export type UserSettingsAudience = "app" | "employee";

/** Whose settings: one person, by the number on their badge or their account. */
export type UserSettingsSubject = { audience: UserSettingsAudience; uuid: number };

export type UserSettingListItem = {
  settingId: SettingId;
  name: string;
  category: SettingCategory;
  /** What the value is: the screen picks its editor from this. */
  valueType: ValueTypeName;
  /** True when the setting holds several of them, e.g. the days a pro works. */
  isList: boolean;
  unit?: Unit;
  /** The allowed values, for a setting whose type lists them. */
  choices?: readonly string[];
  defaultValue: SettingValue;
  status: "planned" | "active";
  /** What this person actually gets, resolved the way the app resolves it. */
  value: SettingValue;
  /** Whether the answer is set on them, or is still the default. */
  setHere: boolean;
  /**
   * Whether *this caller* could save a change to this row.
   *
   * The write's own rule, asked on the read, exactly as Feature Enablements
   * does it: the action behind each sub-tab names the feature it needs, and
   * whoever holds it can move the row. The write asks again regardless, so a
   * screen can never offer a toggle the save would reject.
   */
  editable: boolean;
  /** The feature that would let them, for the console to name. */
  needs?: string;
  /** The version of the value set on them. Send it back with a change. */
  valueVersion?: number;
};

/** GET /api/v1/console/user-settings?for=app|employee&uuid=31290388 */
export type ListUserSettingsResponse = {
  audience: UserSettingsAudience;
  settings: readonly UserSettingListItem[];
  subject: { uuid: number; name: string };
};

export type UserSettingChange = {
  settingId: SettingId;
  /** `null` clears it, so the person goes back to the default. */
  value: SettingValue | null;
  version?: number;
};

/** PUT /api/v1/console/user-settings/values — one Save, one transaction. */
export type SaveUserSettingsRequest = {
  subject: UserSettingsSubject;
  reason: string;
  changes: readonly UserSettingChange[];
};

export type SaveUserSettingsResponse = ListUserSettingsResponse & { saved: number };

// ── Permissions (ADR-0019) ───────────────────────────────────────────────
//
// A permission sits on top of a feature: the feature says what a capability
// is, the permission says whether this actor may take the actions under it,
// and how much they may do alone before asking.
//
// Two records, and the second is the point. A permission is normally granted
// to a role, which is how most people get it. An override on one person beats
// every role — and with an expiry it is how somebody covers for a supervisor
// who is away, and stops covering by itself when they are back.
//
// This is not acting on behalf of that supervisor. A deputy with an override
// acts as themselves: their own UUID goes on every audit entry, which is the
// whole difference in accountability.

export type PermissionRecord = { kind: "role"; staffRoleId: string } | { kind: "employee"; uuid: number };

/** Where a permission's answer came from, in the resolver's order. */
export type PermissionSource = "staffMember" | "staffRole" | "allStaff" | "default";

/** One ceiling: no amount is a limit in a currency the holder has none in. */
export type PermissionLimitValue = { currency: string; amount: number };

export type PermissionListItem = {
  settingId: string;
  name: string;
  category: SettingCategory;
  sensitivity: Sensitivity;
  /** The feature it sits on: without that, the permission cannot apply at all. */
  feature: string;
  /** Whether what they lack can be asked for, or simply refuses (ADR-0019 §5). */
  approvable: boolean;
  /** The actions it covers, so a reader can see what it actually permits. */
  actions: readonly string[];
  held: boolean;
  source: PermissionSource;
  /** The roles granting it, when a role is the answer. */
  grantedBy: readonly string[];
  /** Its ceilings, one per currency. Empty means no ceiling at all. */
  limits: readonly PermissionLimitValue[];
  /** Whether the permission has a limit to set. */
  hasLimit: boolean;
  /** When the value set on this record stops applying. Null: until changed. */
  expiresAt: string | null;
  editable: boolean;
  needs?: string;
  valueVersion?: number;
};

/** GET /api/v1/console/permissions?for=role&staffRoleId=… | ?for=employee&uuid=… */
export type ListPermissionsResponse = {
  record: PermissionRecord;
  permissions: readonly PermissionListItem[];
  /** Who or what the record names, echoed back. */
  subject: { label: string; roles?: readonly string[] };
  /** Every role, so the Role tab can offer them without a second call. */
  roles: readonly { staffRoleId: string; name: string }[];
};

export type PermissionChange = {
  settingId: string;
  /** `null` clears the value on this record, so it follows what is broader. */
  held: boolean | null;
  /** The ceilings to set with it. Absent leaves them alone; empty removes them. */
  limits?: readonly PermissionLimitValue[];
  /** When it should stop applying — the supervisor-is-away case. */
  expiresAt?: string | null;
  version?: number;
};

/** PUT /api/v1/console/permissions/values — one Save, one transaction. */
export type SavePermissionsRequest = {
  record: PermissionRecord;
  reason: string;
  changes: readonly PermissionChange[];
};

export type SavePermissionsResponse = ListPermissionsResponse & { saved: number };

// ── Règles: company rules, versioned (ADR-0013 §2.5) ──────────────────────
//
// Needs EDIT_RULES_STAFF_FEATURE_ENABLEMENT, for reads and writes. A new
// version is CREATE_RULE_VERSION_STAFF_ACTION, audited. `critical` rules need
// an Owner. Versions are only ever added: the database refuses to change or
// delete one.

export type RuleValue = boolean | number | string;

export type RuleVersionItem = {
  ruleVersionId: string;
  ruleId: DefinedRuleId;
  value: RuleValue;
  effectiveFrom: string;
  reason: string;
  legalBasis: string | null;
  setBy: { staffMemberId: string; name: string };
  createdAt: string;
  /** 1 for a rule's first version, then 2, 3… */
  version: number;
};

export type RuleListItem = {
  ruleId: DefinedRuleId;
  category: RuleCategory;
  valueType: ValueTypeName;
  unit: Unit | null;
  defaultValue: RuleValue;
  sensitivity: Sensitivity;
  requiresLegalBasis: boolean;
  status: "active" | "retired";
  /**
   * What applies now: the version with the latest `effectiveFrom` at or
   * before now (the highest version wins a tie), else the default.
   */
  current:
    | { value: RuleValue; source: "default" }
    | { value: RuleValue; source: "version"; ruleVersion: RuleVersionItem };
  /** The next version to take effect, if one is scheduled. */
  next: RuleVersionItem | null;
  /** The highest version number so far, 0 before the first. Send it back when adding a version. */
  version: number;
};

/** GET /api/v1/console/rules. A plain read: not audited. */
export type ListRulesResponse = {
  rules: readonly RuleListItem[];
};

/**
 * GET /api/v1/console/rules/:ruleId/versions
 *
 * Every version, newest version number first. A plain read: not audited.
 */
export type ListRuleVersionsResponse = {
  rule: RuleListItem;
  versions: readonly RuleVersionItem[];
};

/**
 * POST /api/v1/console/rules/:ruleId/versions
 *
 * Adds a version. Refused with `validation_error` (400) when:
 * - `value` doesn't fit the rule's value type (`invalid_value`);
 * - `effectiveFrom` is in the past (`effective_date_in_past`). Up to
 *   RULE_EFFECTIVE_FROM_GRACE_SECONDS in the past counts as now, for clocks
 *   slightly behind;
 * - the rule requires a legal basis and none is given (`legal_basis_required`).
 * `version` is the rule's `version` you were looking at; if another version
 * was added since, it is refused with `stale_version` (409).
 */
export type CreateRuleVersionRequest = {
  value: RuleValue;
  /** ISO 8601 date-time, now or later. Left out: now. */
  effectiveFrom?: string;
  reason: string;
  /** The law, contract or legal advice behind the change. */
  legalBasis?: string | null;
  version: number;
};

export type CreateRuleVersionResponse = {
  rule: RuleListItem;
  ruleVersion: RuleVersionItem;
};

export const RULE_EFFECTIVE_FROM_GRACE_SECONDS = 60;

// ── Teams (WALLIYO-0131) ──────────────────────────────────────────────────
//
// A role is what somebody is trained and trusted to do, so it is the same
// wherever they sit. A team is whose job the work is. Neither can be expressed
// as the other: one role appears on many teams, and one team holds many roles.
//
// Leading a team is a fact on the membership, not a role and not a permission
// count. A role per team gives Engineering Manager, HR Manager, Payments
// Manager, each with near-identical permissions; and a cover grant (ADR-0019,
// an override with an expiry) hands somebody a supervisor's permissions for a
// week without making them anybody's boss.

/** Somebody on a team, as the Teams tab lists them. */
export type TeamMemberItem = {
  /** The number on their badge: what the console searches people by. */
  uuid: number;
  staffMemberId: string;
  name: string;
  /** What they are trained to do. The same on every team they are on. */
  roles: readonly string[];
  /** Whose people these are: this person leads this team. */
  leads: boolean;
  /** False once they have been deactivated: shown, struck through, removable. */
  active: boolean;
};

export type TeamListItem = {
  staffTeamId: string;
  name: string;
  /** What the team is for, in one line. Null until somebody writes it. */
  purpose: string | null;
  memberCount: number;
  /** The names of whoever leads it, for the list before you open one. */
  leadNames: readonly string[];
  /** Closed teams are kept, never deleted, like every audited record. */
  active: boolean;
  createdAt: string;
  /** Send it back with every change: a stale one is refused. */
  version: number;
};

/** GET /api/v1/console/teams (MANAGE_TEAMS). A plain read: not audited. */
export type ListTeamsResponse = {
  teams: readonly TeamListItem[];
  /** Whether the reader may change any of this, so rows can be disabled. */
  editable: boolean;
};

/**
 * GET /api/v1/console/teams/:staffTeamId
 *
 * One team and everybody on it, plus the employees who are not, so the screen
 * can offer them without a second read.
 */
export type GetTeamResponse = {
  team: TeamListItem;
  members: readonly TeamMemberItem[];
  /** Everyone not on this team, to add from. */
  available: readonly TeamMemberItem[];
  editable: boolean;
};

/** POST /api/v1/console/teams (MANAGE_TEAMS). A name already used is refused (409). */
export type CreateTeamRequest = {
  name: string;
  purpose?: string | null;
  /**
   * Who is on it from the moment it exists.
   *
   * A team with nobody on it owns no work and covers nothing, and whoever is
   * creating one already knows who it is for. So the members come with it, in
   * the same transaction: the team and its people appear together or not at
   * all. Everybody listed is a member; `leads` says whose people they are.
   */
  members?: readonly { staffMemberId: string; leads: boolean }[];
  reason: string;
};

export type CreateTeamResponse = {
  team: TeamListItem;
};

/** PATCH /api/v1/console/teams/:staffTeamId (MANAGE_TEAMS). */
export type EditTeamRequest = {
  name?: string;
  purpose?: string | null;
  /** Closing a team empties it of nothing: its members stay recorded. */
  active?: boolean;
  reason: string;
  version: number;
};

export type EditTeamResponse = {
  team: TeamListItem;
};

/** One row of the membership table as the screen sends it back. */
export type TeamMembershipChange = {
  staffMemberId: string;
  /** On the team at all. False removes them. */
  member: boolean;
  /** Whose people these are. Only meaningful when `member`. */
  leads: boolean;
};

/**
 * PUT /api/v1/console/teams/:staffTeamId/members (MANAGE_TEAMS)
 *
 * The whole table in one Save, one transaction: every change is authorised
 * before any of them runs, and they commit or roll back together.
 *
 * Refused with `validation_error` (400) when a change names somebody twice
 * (`duplicate_change`), or names a lead who is not a member
 * (`lead_must_be_member`). Refused with `stale_version` (409) when the team
 * moved while you were looking.
 */
export type SaveTeamMembersRequest = {
  changes: readonly TeamMembershipChange[];
  reason: string;
  version: number;
};

export type SaveTeamMembersResponse = GetTeamResponse & {
  /** How many rows actually moved. */
  saved: number;
};
